/* Sprint 2R-3B — Admin Follow-up Cases panel. */
import React from "react";
import { adminApi } from "../../lib/api";
import { Loader2, AlertOctagon, RefreshCw, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const SEV_STYLE = {
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  conversation_required: "bg-orange-100 text-orange-800 border-orange-200",
  high_risk: "bg-red-100 text-red-700 border-red-200",
  blocked_pending_review: "bg-red-600 text-white border-red-700",
};

const TRIGGER_LABEL = {
  complaint_threshold: "Klager",
  return_rate: "Returrate",
  failed_delivery_rate: "Forgæves rate",
  missed_deadline: "Deadline-fejl",
  late_start: "Sen start",
  no_show: "No-show",
  scan_mismatch: "Scan-mismatch",
  rule_violation: "Regelbrud",
};

export default function FollowupCasesPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("open_only");
  const [openCase, setOpenCase] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = filter === "open_only" ? "?status=open" : "";
      const r = await adminApi().get(`/admin/followup/cases${statusFilter}`);
      setItems(r.data.items || []);
    } catch {
      toast.error("Kunne ikke hente follow-up sager");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  const recomputeAll = async () => {
    try {
      const r = await adminApi().post("/admin/followup/recompute");
      toast.success(`Genberegnet ${r.data.recomputed} chauffører · ${r.data.cases_count} sager opdateret`);
      load();
    } catch {
      toast.error("Genberegn fejlede");
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser sager...</div>;

  const counts = {
    blocked: items.filter(i => i.severity === "blocked_pending_review").length,
    high_risk: items.filter(i => i.severity === "high_risk").length,
    conversation: items.filter(i => i.severity === "conversation_required").length,
    warning: items.filter(i => i.severity === "warning").length,
  };

  return (
    <div className="space-y-6" data-testid="followup-cases-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-xl text-brand-dark flex items-center gap-2">
            <AlertOctagon size={20} className="text-brand-red"/> Follow-up sager
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            Automatiske opfølgningssager fra klager + KPI-overskridelser. {items.length} sager vist.
          </p>
        </div>
        <div className="flex gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="px-3 py-2 border border-brand text-sm bg-white">
            <option value="open_only">Kun åbne (status=open)</option>
            <option value="all">Alle aktive</option>
          </select>
          <button onClick={recomputeAll}
                  className="px-3 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm inline-flex items-center gap-2"
                  data-testid="followup-recompute-all">
            <RefreshCw size={14}/> Genberegn alle
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Blokerede", val: counts.blocked, color: "bg-red-600 text-white" },
          { label: "High Risk", val: counts.high_risk, color: "bg-red-500 text-white" },
          { label: "Samtale påkrævet", val: counts.conversation, color: "bg-orange-500 text-white" },
          { label: "Advarsler", val: counts.warning, color: "bg-amber-500 text-white" },
        ].map((c) => (
          <div key={c.label} className={`p-3 ${c.color}`}>
            <div className="text-2xl font-bold">{c.val}</div>
            <div className="text-xs uppercase tracking-wide">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm" data-testid="followup-cases-table">
          <thead>
            <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
              <th className="p-3">Chauffør</th>
              <th className="p-3">Trigger</th>
              <th className="p-3">Severity</th>
              <th className="p-3">Værdi / tærskel</th>
              <th className="p-3">Ansvar</th>
              <th className="p-3">Status</th>
              <th className="p-3">Oprettet</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-brand-text2">Ingen åbne sager 🎉</td></tr>}
            {items.map((c) => (
              <tr key={c.case_id} className="border-b border-gray-100 hover:bg-brand-bg2 cursor-pointer"
                  onClick={() => setOpenCase(c)} data-testid={`followup-case-${c.case_id}`}>
                <td className="p-3">
                  <div className="font-semibold">{c.driver_name || c.driver_id?.slice(0,8)}</div>
                  <div className="text-xs text-brand-text2">{c.driver_type}</div>
                </td>
                <td className="p-3">{TRIGGER_LABEL[c.trigger_type] || c.trigger_type}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase font-bold px-2 py-1 border ${SEV_STYLE[c.severity] || ""}`}>
                    {c.severity}
                  </span>
                </td>
                <td className="p-3 text-xs">{c.trigger_value} / {c.threshold_value}</td>
                <td className="p-3 text-xs">{c.responsibility_status?.replace(/_/g," ")}</td>
                <td className="p-3 text-xs">{c.status}</td>
                <td className="p-3 text-xs text-brand-text2">{new Date(c.created_at).toLocaleDateString("da-DK")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openCase && <CaseDetailModal caseDoc={openCase} onClose={() => setOpenCase(null)} onChange={load}/>}
    </div>
  );
}

function CaseDetailModal({ caseDoc, onClose, onChange }) {
  const [busy, setBusy] = React.useState(false);
  const [reason, setReason] = React.useState("");

  const classify = async (responsibility) => {
    if (reason.length < 4) return toast.error("Begrundelse skal være min. 4 tegn");
    setBusy(true);
    try {
      await adminApi().post(`/admin/followup/cases/${caseDoc.case_id}/classify`,
        { responsibility, reason });
      toast.success("Ansvar klassificeret");
      onChange();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (e?.message || "Fejl"));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (responsibility) => {
    if (reason.length < 4) return toast.error("Begrundelse skal være min. 4 tegn");
    setBusy(true);
    try {
      await adminApi().post(`/admin/followup/cases/${caseDoc.case_id}/dismiss`,
        { responsibility, reason });
      toast.success("Sag afvist");
      onChange();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (e?.message || "Fejl"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl mx-4 border border-brand shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Follow-up sag</h3>
          <button onClick={onClose}><XCircle size={18}/></button>
        </div>
        <div className="p-4 space-y-4" data-testid="followup-case-detail">
          <div>
            <div className="text-xs text-brand-text2 uppercase">Chauffør</div>
            <div className="font-semibold">{caseDoc.driver_name || caseDoc.driver_id}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><b>Trigger:</b> {TRIGGER_LABEL[caseDoc.trigger_type] || caseDoc.trigger_type}</div>
            <div><b>Severity:</b> {caseDoc.severity}</div>
            <div><b>Værdi:</b> {caseDoc.trigger_value} / {caseDoc.threshold_value}</div>
            <div><b>Periode:</b> {caseDoc.trigger_period_days} dage</div>
            <div><b>Ansvar:</b> {caseDoc.responsibility_status}</div>
            <div><b>Status:</b> {caseDoc.status}</div>
          </div>
          {caseDoc.extra && Object.keys(caseDoc.extra).length > 0 && (
            <div>
              <div className="text-xs text-brand-text2 uppercase mb-1">Detaljer</div>
              <pre className="text-xs bg-brand-bg2 p-2 overflow-x-auto">{JSON.stringify(caseDoc.extra, null, 2)}</pre>
            </div>
          )}
          <div>
            <label className="text-xs text-brand-text2 uppercase mb-1 block">Begrundelse (min. 4 tegn)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-brand text-sm"
              rows={3}
              placeholder="Hvorfor klassificerer / afviser du?"
              data-testid="followup-case-reason"
            />
          </div>
          <div className="space-y-2">
            <div className="text-xs uppercase text-brand-text2">Klassificer ansvar (reducerer kun score ved driver_responsibility eller vognmand_responsibility)</div>
            <div className="flex flex-wrap gap-2">
              {[
                ["driver_responsibility", "Chauffør", "bg-red-600 text-white"],
                ["vognmand_responsibility", "Vognmand", "bg-red-500 text-white"],
                ["customer_responsibility", "Kunde", "bg-blue-600 text-white"],
                ["HUB_responsibility", "HUB", "bg-purple-600 text-white"],
                ["planning_system_responsibility", "Planlægning", "bg-slate-600 text-white"],
                ["external_factor", "Eksternt", "bg-slate-500 text-white"],
              ].map(([r, lbl, color]) => (
                <button key={r} onClick={() => classify(r)} disabled={busy}
                        className={`px-3 py-1.5 text-xs ${color} hover:opacity-90 disabled:opacity-50`}
                        data-testid={`classify-${r}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="pt-3 border-t border-gray-100">
            <button onClick={() => dismiss("unknown_pending_review")} disabled={busy}
                    className="px-3 py-1.5 text-xs bg-white border border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="dismiss-case">
              <XCircle size={12} className="inline mr-1"/> Afvis sag (ingen score-impact)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
